import { UAParser } from "ua-parser-js";
import { EVENT_NAMES } from "../types/event-names";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getDecodedAccessToken } from "../utils";

export type GAEventPayload = {
  name: string;
  params?: Record<string, any>;
};

// Event names
export type GAEventName = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];

// User agent parser
const parser = new UAParser();
const ua = parser.getResult();

// Track event
export const trackEvent = (eventName: GAEventName, params?: Record<string, any>) => {
  sendGAEvent({ name: eventName, params });
};

// Get universal params
export const getUniversalParams = () => ({
  user_id: getUserId(),
  timestamp: new Date().getTime(),
  platform: getPlatform(),
  device_type: ua.device.type || "desktop",
  os: `${ua.os.name} ${ua.os.version}`,
  browser: `${ua.browser.name} ${ua.browser.version}`,
  plan_type: getUserPlanType(),
  language: navigator.language || "en",
  country: getUserCountry(),
});

// Send event
export const sendGAEvent = ({ name, params = {} }: GAEventPayload) => {
  // const isDev = process.env.NODE_ENV === 'development';

  const payload = {
    name,
    ...getUniversalParams(),
    ...params,
  };

  // if (isDev) {
  // console.info(`[GA DEBUG] Event: ${name}`, payload);
  // }

  // Also send event to Fortect logging endpoint
  const url = new URL("https://app.fortect.com/events/portal-events.php");

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value));
    }
  });

  url.searchParams.append("id", name);
  url.searchParams.append("version", "1.0.18");

  fetch(url.toString())
    .then((res) => {
      if (!res.ok) {
        console.warn("[PortalEvents] Failed to log event", res.status);
      }
    })
    .catch((err) => {
      console.warn("[PortalEvents] Network error", err);
    });

  if (typeof window.gtag === "function") {
    window.gtag("event", name, payload);
  } else {
    console.warn("gtag.js not loaded");
  }
};

// Use page view tracking
export const usePageViewTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: document.title,
        ...getUniversalParams(),
      });
    }
  }, [location]);
};

// Get user id
function getUserId(): string {
  return getDecodedAccessToken()?.email || localStorage.getItem("user_id") || "anon_" + generateUUID();
}

// Get platform
function getPlatform(): string {
  if (window.navigator.userAgent.includes("Electron")) return "desktop_app";

  const deviceType = ua.device.type;

  switch (deviceType) {
    case "mobile":
      return "mobile_web";
    case "tablet":
      return "tablet_web";
    case "smarttv":
      return "tv_web";
    default:
      return "web";
  }
}

// Get user plan type <- TODO Finish this
function getUserPlanType(): string {
  return localStorage.getItem("plan_type") || "not detected";
}

// Get user country
function getUserCountry(): string {
  return localStorage.getItem("user_country") || "US";
}

// Generate UUID
function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
